<template>
  <div class="c-pagination">
    <!-- Actions -->
    <a
      :href="paginationHrefs?.first"
      class="c-pagination__action desktop"
      :class="{ disabled: isPrevDisabled }"
      @click.prevent="setActive(1, isPrevDisabled)"
    >
      <CIcon name="first_page" />
    </a>
    <a
      :href="paginationHrefs?.prev"
      class="c-pagination__action"
      :class="{ disabled: isPrevDisabled }"
      @click.prevent="setActive(active - 1, isPrevDisabled)"
    >
      <CIcon name="chevron_left" />
    </a>
    <!-- Pages -->
    <a
      v-for="page in pagination"
      :key="page"
      :href="paginationHrefs[page.toString()]"
      class="c-pagination__item"
      :class="{ active: active == page, disabled: page === ellipsis }"
      @click.prevent="setActive(page, page === ellipsis)"
    >
      {{ page }}
    </a>
    <!-- Actions -->
    <a
      class="c-pagination__action"
      :href="paginationHrefs?.next"
      :class="{ disabled: isNextDisabled }"
      @click.prevent="setActive(active + 1, isNextDisabled)"
    >
      <CIcon name="chevron_right" />
    </a>
    <a
      class="c-pagination__action desktop"
      :href="paginationHrefs?.last"
      :class="{ disabled: isNextDisabled }"
      @click.prevent="setActive(pages, isNextDisabled)"
    >
      <CIcon name="last_page" />
    </a>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import CIcon from '../../atoms/CIcon'

const router = useRouter()
const route = useRoute()

type Props = {
  pages: number
  active?: number
}

const props = withDefaults(defineProps<Props>(), {
  pages: 1,
  active: 1,
})
const emit = defineEmits<{
  (e: 'update:model-value', value: number): void
}>()

const ellipsis = '...'

const pagination = computed((): (string | number)[] => {
  const paginationItems: Array<number | string> = []
  const pages: number = props.pages
  const activePageIndex: number = props.active
  const maxItems = 4

  if (pages <= maxItems) {
    for (let i = 1; i <= pages; i++) {
      paginationItems.push(i)
    }
  } else {
    // Adding 1
    paginationItems.push(1)

    if (activePageIndex > 3) {
      paginationItems.push(ellipsis)
    }

    let start = Math.max(2, activePageIndex - 1)
    let end = Math.min(
      pages - 1,
      activePageIndex === 1 ? activePageIndex + 2 : activePageIndex + 1,
    )
    if (end - start + 1 < maxItems - 2) {
      if (activePageIndex - start < end - activePageIndex) {
        end = Math.min(end + (maxItems - 2 - (end - start)), pages - 1)
      } else {
        start = Math.max(2, start - (maxItems - 3 - (end - start)))
      }
    }

    for (let i = start; i <= end; i++) {
      paginationItems.push(i)
    }

    if (end < pages - 1) {
      paginationItems.push(ellipsis)
    }

    // Adding last nr
    paginationItems.push(pages)
  }

  return paginationItems
})

const getHref = (pageNumber: number | string): string => {
  return router.resolve({
    path: route.path,
    query: { ...route.query, page: pageNumber },
  }).fullPath
}

const paginationHrefs = computed(() => {
  const hrefsObj: Record<string, string> = {
    first: getHref(1),
    prev: getHref(props.active - 1 === 0 ? 1 : props.active - 1),
    next: getHref(
      props.active + 1 === props.pages ? props.pages : props.active + 1,
    ),
    last: getHref(props.pages),
  }

  if (Array.isArray(pagination.value)) {
    for (const page of pagination.value) {
      if (page !== ellipsis) {
        hrefsObj[page.toString()] = getHref(page)
      }
    }
  }

  return hrefsObj
})

const isPrevDisabled = computed(() => props.active === 1)
const isNextDisabled = computed(() => props.active === props.pages)

const setActive = (i: number, isDisabled: boolean) => {
  if (!isDisabled) {
    emit('update:model-value', i)
  }
}
</script>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export default defineComponent({
  name: 'CPagination',
})
</script>
